import type {
  AgentActionPayload,
  PresetActionId,
  PresetOption,
  PresetPresentationType,
} from "@/types/createAgent";

export const PRIMARY_PRESET_ACTIONS = [
  "generate_dress_front",
  "generate_dress_back",
  "submit_quote",
  "buy_credits_back",
  "buy_credits_modify",
] as const satisfies readonly PresetActionId[];

export const SECONDARY_PRESET_ACTIONS = [
  "start_virtual_try_on",
  "continue_to_back_design",
  "start_over",
  "request_location",
  "modify_dress_front",
  "modify_dress_back",
  "modify_field",
] as const satisfies readonly PresetActionId[];

export const LOW_PRIORITY_PRESET_ACTIONS = [
  "set_preset_type",
  "set_color",
  "set_length",
  "set_sleeve",
  "set_neckline",
  "set_silhouette",
  "set_fabric",
  "set_back_prompt",
  "set_back_style",
  "suggest_field",
  "custom",
] as const satisfies readonly PresetActionId[];

export const PRESET_ACTION_ORDER = [
  ...PRIMARY_PRESET_ACTIONS,
  ...SECONDARY_PRESET_ACTIONS,
  ...LOW_PRIORITY_PRESET_ACTIONS,
] as const satisfies readonly PresetActionId[];

export const PRESET_ACTION_IDS =
  PRESET_ACTION_ORDER as readonly PresetActionId[];

export const CREDIT_PRESET_ACTIONS = [
  "generate_dress_front",
  "generate_dress_back",
  "modify_dress_front",
  "modify_dress_back",
] as const satisfies readonly PresetActionId[];

export const COMMIT_PRESET_ACTIONS = [
  ...CREDIT_PRESET_ACTIONS,
  "submit_quote",
  "start_virtual_try_on",
  "buy_credits_back",
  "buy_credits_modify",
  "continue_to_back_design",
  "request_location",
  "start_over",
] as const satisfies readonly PresetActionId[];

export const FIELD_CHOICE_PRESET_ACTIONS = [
  "set_preset_type",
  "set_color",
  "set_length",
  "set_sleeve",
  "set_neckline",
  "set_silhouette",
  "set_fabric",
  "set_back_prompt",
  "set_back_style",
] as const satisfies readonly PresetActionId[];

export const CATEGORY_GRID_PRESET_ACTIONS = [
  "set_length",
  "set_sleeve",
  "set_neckline",
  "set_silhouette",
  "set_fabric",
  "set_back_prompt",
  "set_back_style",
] as const satisfies readonly PresetActionId[];

export const PRESET_ACTION_IDS_BY_ACTION = {
  action: [
    "generate_dress_front",
    "generate_dress_back",
    "submit_quote",
    "start_virtual_try_on",
    "request_location",
    "buy_credits_back",
    "buy_credits_modify",
    "continue_to_back_design",
    "start_over",
  ],
  suggest: [
    "set_preset_type",
    "set_color",
    "set_length",
    "set_sleeve",
    "set_neckline",
    "set_silhouette",
    "set_fabric",
    "set_back_prompt",
    "set_back_style",
    "suggest_field",
    "custom",
  ],
  modify: ["modify_dress_front", "modify_dress_back", "modify_field"],
} as const satisfies Record<
  AgentActionPayload["action"],
  readonly PresetActionId[]
>;

export const VIBE_PREVIEW_CELLS = ["color", "shape", "fabric"] as const;

const VIBE_BUNDLE_FIELDS = [
  "color",
  "length",
  "sleeve",
  "neckline",
  "silhouette",
  "fabric",
] as const;

const STRUCTURED_CHOICE_FIELDS = [
  ...VIBE_BUNDLE_FIELDS,
  "sleeveAddons",
  "backPrompt",
] as const;

function includesPresetActionId(
  actionIds: readonly PresetActionId[],
  value: PresetActionId | string
): value is PresetActionId {
  return (actionIds as readonly string[]).includes(value);
}

function getPresetData(preset: PresetOption): Record<string, unknown> {
  return preset.data && typeof preset.data === "object" ? preset.data : {};
}

function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export function getVibeBundleFieldCount(preset: PresetOption): number {
  const data = getPresetData(preset);
  return VIBE_BUNDLE_FIELDS.filter(field => hasValue(data[field])).length;
}

function hasStructuredChoiceField(preset: PresetOption): boolean {
  const data = getPresetData(preset);
  return STRUCTURED_CHOICE_FIELDS.some(field => hasValue(data[field]));
}

function isUploadChoice(preset: PresetOption): boolean {
  const suggestedField = preset.actionData?.suggestedField;
  return (
    suggestedField === "upload" ||
    suggestedField === "image_upload" ||
    suggestedField === "front_upload" ||
    suggestedField === "back_upload"
  );
}

export function isCreditPresetAction(
  actionId: PresetActionId | string
): boolean {
  return includesPresetActionId(CREDIT_PRESET_ACTIONS, actionId);
}

export function isCommitPresetAction(
  actionId: PresetActionId | string
): boolean {
  return includesPresetActionId(COMMIT_PRESET_ACTIONS, actionId);
}

export function isGenerationPresetAction(
  actionId: PresetActionId | string
): boolean {
  return isCreditPresetAction(actionId);
}

export function isCategoryGridPresetAction(
  actionId: PresetActionId | string
): boolean {
  return includesPresetActionId(CATEGORY_GRID_PRESET_ACTIONS, actionId);
}

export function normalizePresetLabel(value: string): string {
  return value.trim().toLowerCase();
}

export function orderPresets<T extends { actionId: PresetActionId }>(
  presets: readonly T[]
): T[] {
  return [...presets]
    .map((preset, index) => ({ preset, index }))
    .sort((a, b) => {
      const aOrder = PRESET_ACTION_ORDER.indexOf(a.preset.actionId);
      const bOrder = PRESET_ACTION_ORDER.indexOf(b.preset.actionId);
      const aRank = aOrder === -1 ? Number.POSITIVE_INFINITY : aOrder;
      const bRank = bOrder === -1 ? Number.POSITIVE_INFINITY : bOrder;
      if (aRank !== bRank) return aRank - bRank;
      return a.index - b.index;
    })
    .map(({ preset }) => preset);
}

export function getCommitPresets<T extends { actionId: PresetActionId }>(
  presets: readonly T[]
): T[] {
  return orderPresets(presets).filter(preset =>
    isCommitPresetAction(preset.actionId)
  );
}

export function getChoicePresets<T extends { actionId: PresetActionId }>(
  presets: readonly T[]
): T[] {
  return orderPresets(presets).filter(
    preset => !isCommitPresetAction(preset.actionId)
  );
}

export function getCategoryGridActionId(
  presets: readonly Pick<PresetOption, "action" | "actionId">[]
): PresetActionId | null {
  if (presets.length === 0) return null;
  const firstActionId = presets[0].actionId;
  if (!isCategoryGridPresetAction(firstActionId)) {
    return null;
  }
  return presets.every(
    preset => preset.action === "suggest" && preset.actionId === firstActionId
  )
    ? firstActionId
    : null;
}

export function getPresetPresentationType(
  preset: PresetOption
): PresetPresentationType {
  if (preset.presetType) return preset.presetType;
  if (isCommitPresetAction(preset.actionId)) return "commit_action";
  if (isUploadChoice(preset)) return "upload_choice";
  if (includesPresetActionId(FIELD_CHOICE_PRESET_ACTIONS, preset.actionId)) {
    return "field_choice";
  }

  if (preset.actionId === "custom") {
    if (getVibeBundleFieldCount(preset) >= 3) return "vibe_bundle";
    if (hasStructuredChoiceField(preset)) return "field_choice";
    return "refinement_choice";
  }

  return "refinement_choice";
}

export function isVibeBundlePreset(preset: PresetOption): boolean {
  return getPresetPresentationType(preset) === "vibe_bundle";
}

export function isRefinementPreset(preset: PresetOption): boolean {
  return getPresetPresentationType(preset) === "refinement_choice";
}
